import type {
  BoxOfferDetailedEntity,
  BoxOfferDraftEntity,
  BoxOfferItemEntity,
} from "@/modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType, OfferAutonomousPurchase } from "@/modules/boxOffer/entities/boxOfferTypes"
import type { BoxSubscriptionDetailsEntity } from "@/modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import boxOfferDropInIcon from "@images/illustrations/boxOfferDropInIcon.svg"
import boxOfferMillenniumIcon from "@images/illustrations/boxOfferMillenniumIcon.svg"
import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import boxOfferTicketIcon from "@images/illustrations/boxOfferTicketIcon.svg"
import boxOfferTrialIcon from "@images/illustrations/boxOfferTrialIcon.svg"
import { BoxOfferDropInIcon } from "@images/svgIcons/boxOffer/dropInIcon"
import { BoxOfferFlagIcon } from "@images/svgIcons/boxOffer/flagIcon"
import { BoxOfferMilleniumIcon } from "@images/svgIcons/boxOffer/milleniumIcon"
import { BoxOfferPassIcon } from "@images/svgIcons/boxOffer/passIcon"
import { BoxOfferPlanIcon } from "@images/svgIcons/boxOffer/planIcon"
import { HiddenPriceIcon } from "@images/svgIcons/essentials/hiddenPriceIcon"
import { MoneyBillIcon } from "@images/svgIcons/moneyBillIcon"
import { SlashedEyeLineIcon } from "@images/svgIcons/slashedEyeLineIcon"
import { word } from "@modules/core/utils/i18n"
import dayjs from "dayjs"
import { Archive } from "lucide-react"
/**
 * Utility function to get the icon of the box offer by type
 * @param type : BoxOfferType
 * @returns React.JSX.Element
 */
export function getBoxOfferIcon(type: BoxOfferType): React.JSX.Element {
  switch (type) {
    case BoxOfferType.Subscription:
      return <img src={boxOfferPlanIcon} alt="boxOfferPlanIcon" />
    case BoxOfferType.TicketsBook:
      return <img src={boxOfferTicketIcon} alt="boxOfferTicketIcon" />
    case BoxOfferType.Trial:
      return <img src={boxOfferTrialIcon} alt="boxOfferTrialIcon" />
    case BoxOfferType.DropIn:
      return <img src={boxOfferDropInIcon} alt="boxOfferDropInIcon" />
    case BoxOfferType.Millenium:
      return <img src={boxOfferMillenniumIcon} alt="boxOfferMillenniumIcon" />
    default:
      return <></>
  }
}
/**
 * Utility function to get the commitment of the box offer with the validityPeriodMonths or minimumMonthlyDuration
 * @param offer : BoxOfferDetailedEntity | BoxOfferDraftEntity | null
 * @returns string
 */
export const getOfferCommitment = (offer: BoxOfferDetailedEntity | BoxOfferDraftEntity | null): string => {
  if (offer && [BoxOfferType.Trial, BoxOfferType.TicketsBook, BoxOfferType.DropIn].includes(offer.type)) {
    if (offer.validityPeriodMonths) {
      return word("box.offer.form.infos.validity", { count: offer.validityPeriodMonths })
    }
    return word("box.offer.form.infos.validity.unlimited")
  } else if (offer && "minimumMonthlyDuration" in offer && offer.minimumMonthlyDuration) {
    return word("box.offer.form.infos.commitment", { count: offer.minimumMonthlyDuration })
  }
  return word("box.offer.form.infos.no.commitment")
}

export const getSubscriptionCommitmentPeriod = (subscription: BoxSubscriptionDetailsEntity): string => {
  if (subscription.minimumCommitmentPeriod) {
    return word("box.offer.form.infos.commitment", {
      count: dayjs.duration(subscription.minimumCommitmentPeriod).months(),
    })
  }
  return word("box.offer.form.infos.no.commitment")
}

/**
 * Utility function to get the labels for the recurring period input select inside the offer form
 * @param period : RecurringPeriod | null
 * @returns string (e.g., "every month", "every 2 weeks")
 */
export const getRecurringPeriodInputLabel = (period: string | null | undefined): string => {
  if (!period) {
    period = "P1M"
  }

  const weeks = dayjs.duration(period).asWeeks()
  const months = dayjs.duration(period).asMonths()

  if (weeks >= 1 && months < 1) {
    return word(`box.offer.form.recurring_period.weeks`, { count: Math.round(weeks) })
  }

  if (months >= 1) {
    return word(`box.offer.form.recurring_period.months`, { count: Math.round(months) })
  }

  return word("box.offer.form.recurring_period.months", { count: 1 })
}

/**
 * Utility function to get a short recurring period label for offer price display
 * @param period : RecurringPeriod | null
 * @returns string (e.g., "/ month", "/ 2 weeks")
 */
export const getRecurringPeriodShortLabel = (period: string | null | undefined): string => {
  if (!period) {
    period = "P1M"
  }

  const weeks = dayjs.duration(period).asWeeks()
  const months = dayjs.duration(period).asMonths()

  if (weeks >= 1 && months < 1) {
    const numberOfWeek = Math.round(weeks)
    return `/ ${numberOfWeek >= 2 ? `${numberOfWeek} ` : ""}${word(`global.week.label`, { count: numberOfWeek })}`
  }

  if (months >= 1) {
    const numberOfMonth = Math.round(months)
    return `/ ${numberOfMonth >= 2 ? `${numberOfMonth} ` : ""}${word(`global.month.label`, { count: numberOfMonth })}`
  }

  return `/ ${word("global.month.label", { count: 1 })}`
}

export const getOfferVisibility = (
  offer: BoxOfferItemEntity | BoxOfferDetailedEntity | BoxOfferDraftEntity | null,
  color: string,
) => {
  if (offer?.archived) {
    return {
      text: word("global.archived", { count: 1 }),
      icon: <Archive strokeWidth={1.5} size={14} color={color} />,
    }
  }
  if (offer?.showcaseDisplay === false)
    return {
      text: word("box.offer.form.resume.showcaseDisplay"),
      icon: <SlashedEyeLineIcon color={color} />,
    }

  if (offer?.priceDisplay === false)
    return {
      text: word("box.offer.form.resume.priceDisplay"),
      icon: <HiddenPriceIcon color={color} />,
    }

  const iconMoney = <MoneyBillIcon color={color} />
  switch (offer?.autonomousPurchase) {
    case OfferAutonomousPurchase.PUBLIC:
      return {
        text: word("box.offer.form.resume.public"),
        icon: iconMoney,
      }
    case OfferAutonomousPurchase.LEADS_AND_DROP_IN:
      return {
        text: word("box.offer.form.resume.leadsAndDropIn"),
        icon: iconMoney,
      }
    case OfferAutonomousPurchase.MEMBERS:
      return {
        text: word("box.offer.form.resume.members"),
        icon: iconMoney,
      }
    case OfferAutonomousPurchase.NO_ONE:
      return {
        text: word("box.offer.form.resume.noOne"),
        icon: iconMoney,
      }
  }

  return {
    text: "",
    icon: null,
  }
}

export const getOfferTypeIcon = (type: BoxOfferType, color: string, size: number): React.JSX.Element => {
  switch (type) {
    case BoxOfferType.Subscription:
      return <BoxOfferPlanIcon size={size} color={color} />
    case BoxOfferType.TicketsBook:
      return <BoxOfferPassIcon size={size} color={color} />
    case BoxOfferType.Trial:
      return <BoxOfferFlagIcon size={size} color={color} />
    case BoxOfferType.DropIn:
      return <BoxOfferDropInIcon size={size} color={color} />
    case BoxOfferType.Millenium:
      return <BoxOfferMilleniumIcon size={size} color={color} />
    default:
      return <></>
  }
}
